import { ExpandMore, MailOutline } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  GridLegacy as Grid,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { LegacyRichTextarea } from '~/legacy-components/LegacyRichTextarea';

import {
  Props,
  getFirstErrorMessage,
  useSendEmailComponent,
} from './useSendEmailComponent';
import { useSxStyles } from './useStyles';

const EMAIL_FORM_ID = 'send-email-form';
const EMAIL_RECIPIENTS_PLACEHOLDER =
  'ex: john.doe@gmail.com,jane.doe@gmail.com';

export const LegacyEmailDialog = ({ open, onClose }: Props) => {
  const {
    form: {
      control,
      formState: { errors, isValid, isSubmitted },
      watch,
    },
    allEmployees,
    orgUnits,
    loading,
    onDialogClose,
    onSubmit,
    updateRecipientsCount,
    recipientsSectionState,
  } = useSendEmailComponent({ open, onClose });
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const test = Object.keys(allEmployees).map((empId) => parseInt(empId, 10));

  const recipientsCount = watch('recipientCount');

  const errorMessage = getFirstErrorMessage(errors);
  const hasError = !!errorMessage;

  return (
    <>
      <DraggableDialog open={open} onClose={onDialogClose} maxWidth={'lg'}>
        <SolaForceDialogTitle onClose={onDialogClose} icon={<MailOutline />}>
          {t('laSendEmail')}
        </SolaForceDialogTitle>
        <DialogContent dividers={true} sx={sxStyles.dialogContent}>
          {loading ? (
            <CircularProgress />
          ) : (
            <form id={EMAIL_FORM_ID} onSubmit={onSubmit}>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <Accordion
                    expanded={recipientsSectionState.isOpen}
                    onChange={recipientsSectionState.toggle}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={
                        !!errors?.recipientCount?.message
                          ? sxStyles.accordionError
                          : undefined
                      }
                    >{`${t(
                      'laRecipients',
                    )} (${recipientsCount})`}</AccordionSummary>
                    <Divider />
                    <AccordionDetails>
                      <Grid
                        container
                        item
                        alignItems="center"
                        spacing={1}
                        sx={sxStyles.recipientsContainer}
                      >
                        <Grid item xs={12}>
                          <Controller
                            control={control}
                            name="units"
                            render={({
                              field: { onChange, value },
                              fieldState: { error },
                            }) => (
                              <Autocomplete
                                placeholder={`${t('laAddUnits')}`}
                                options={orgUnits}
                                getOptionLabel={(c) => c.fOrgUnitName}
                                onChange={(e, value) => {
                                  onChange(value.map((v) => v.fOrgUnitId));
                                  updateRecipientsCount();
                                }}
                                value={
                                  orgUnits.filter((o) =>
                                    value?.includes(o.fOrgUnitId),
                                  ) || []
                                }
                                multiple={true}
                                fullWidth={true}
                                size={'small'}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={t('laUnits')}
                                    error={!!error}
                                    helperText={error?.message}
                                  />
                                )}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Controller
                            control={control}
                            name="employees"
                            render={({
                              field: { onChange, value },
                              fieldState: { error },
                            }) => (
                              <Autocomplete
                                placeholder={`${t('laAddEmployees')}`}
                                options={test}
                                getOptionLabel={(c) =>
                                  `${allEmployees[c].fEmpFirstName} ${allEmployees[c].fEmpLastName}`
                                }
                                onChange={(e, value) => {
                                  onChange(value);
                                  updateRecipientsCount();
                                }}
                                value={value as number[]}
                                multiple={true}
                                fullWidth={true}
                                size={'small'}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={t('laEmployees')}
                                    error={!!error}
                                    helperText={error?.message}
                                  />
                                )}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Controller
                            control={control}
                            name="emails"
                            defaultValue=""
                            render={({
                              field: { onChange, value },
                              fieldState: { error },
                            }) => (
                              <TextField
                                label={t('laEmail')}
                                type="text"
                                value={value}
                                placeholder={EMAIL_RECIPIENTS_PLACEHOLDER}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  onChange(e.target.value);
                                  updateRecipientsCount();
                                }}
                                fullWidth={true}
                                size={'small'}
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        </Grid>
                        {!!errors?.recipientCount?.message && (
                          <Grid item>
                            <Typography color={'error'}>
                              {errors?.recipientCount?.message}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                <Grid item>
                  <Controller
                    control={control}
                    name="subject"
                    defaultValue=""
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        type="text"
                        label={t('laSubject')}
                        value={value}
                        onChange={onChange}
                        fullWidth={true}
                        size={'small'}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  <Typography variant="subtitle2">
                    {`${t('laBody')}:`}
                  </Typography>
                  <Controller
                    control={control}
                    name="body"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <>
                        <LegacyRichTextarea value={value} onChange={onChange} />
                        {!!error && (
                          <Typography color={'error'}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>
        <DialogActions sx={sxStyles.dialogActions}>
          <Box sx={sxStyles.dialogError}>
            {hasError && (
              <Typography color={'error'}>{errorMessage}</Typography>
            )}
          </Box>
          <Box>
            <Button onClick={onSubmit} disabled={isSubmitted && !isValid}>
              {t('laSend')}
            </Button>
            <Button onClick={onDialogClose}>{t('laClose')}</Button>
          </Box>
        </DialogActions>
      </DraggableDialog>
    </>
  );
};
